"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/modals/dialog";

import { MatchOverview } from "../match-overview";

export interface MatchOverviewDialogProps {
  tournamentId: string;
  /** Открытый матч; `null` — модалка закрыта. */
  matchId: string | null;
  onClose: () => void;
}

/**
 * Матч в модалке над сеткой.
 *
 * Берём общий `Dialog` из `@/shared/ui` (shadcn поверх Radix) — он бесплатно
 * даёт центрирование, оверлей, закрытие по клику снаружи и Escape, ловушку
 * фокуса и `aria-modal`. Свой оверлей писать нельзя: потеряется вся эта
 * семантика.
 *
 * Внутри — тот же `MatchOverview`, что и на странице матча. Шапки турнира и
 * «Назад к сетке» здесь нет намеренно: это части страницы-обёртки, а сетка и
 * так под модалкой.
 *
 * Высота: сам диалог не выше вьюпорта (`max-h`), прокручивается только тело —
 * иначе на 1280×720 и на телефоне низ карточки уезжал бы за экран вместе с
 * крестиком.
 */
export function MatchOverviewDialog({
  tournamentId,
  matchId,
  onClose,
}: MatchOverviewDialogProps) {
  const tUi = useTranslations("Ui");
  // Radix держит содержимое смонтированным до конца анимации закрытия.
  // Без запомненного id блок на эти ~200 мс схлопывался бы в скелетон.
  const lastMatchId = useRef<string | null>(matchId);
  useEffect(() => {
    if (matchId) lastMatchId.current = matchId;
  }, [matchId]);
  const renderedMatchId = matchId ?? lastMatchId.current;

  return (
    <Dialog
      open={Boolean(matchId)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        {/* pr-12 — чтобы заголовок не залезал под крестик. */}
        <DialogHeader className="shrink-0 border-b border-border px-4 py-3 pr-12 sm:px-6">
          <DialogTitle className="text-base">{tUi("matchOverviewDialog.match")}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
          {renderedMatchId && (
            <MatchOverview
              tournamentId={tournamentId}
              matchId={renderedMatchId}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
